import { useEffect, useState } from "react"
import {
  collectionGroup,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  DocumentData,
  QueryDocumentSnapshot,
  doc,
  getDoc
} from "firebase/firestore"
import { db } from "../../../../services/firebase"
import "../style/AdminTrades.scss"

type Trade = {
  id: string
  userId: string
  userEmail: string
  symbol: string
  side: string
  amount: number
  price: number
  createdAt: string
}

const PAGE_SIZE = 10

const TradesPanel = () => {
  const [trades, setTrades] = useState<Trade[]>([])
  const [loading, setLoading] = useState(false)
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null)
  const [pageStack, setPageStack] = useState<QueryDocumentSnapshot<DocumentData>[]>([])

  useEffect(() => {
    loadTrades()
  }, [])

  const loadTrades = async (
    cursor?: QueryDocumentSnapshot<DocumentData>,
    direction?: "next" | "prev"
  ) => {
    setLoading(true)
    try {
      let q = query(
        collectionGroup(db, "trades"),
        orderBy("createdAt", "desc"),
        limit(PAGE_SIZE)
      )

      if (direction === "next" && cursor) {
        q = query(
          collectionGroup(db, "trades"),
          orderBy("createdAt", "desc"),
          startAfter(cursor),
          limit(PAGE_SIZE)
        )
      }

      const snapshot = await getDocs(q)

      const promises = snapshot.docs.map(async (docSnap) => {
        const trade = docSnap.data()
        const userId = docSnap.ref.parent.parent?.id || "unknown"
        let userEmail = "Unknown"

        try {
          const userDoc = await getDoc(doc(db, "users", userId))
          if (userDoc.exists()) {
            userEmail = userDoc.data().email || "Unknown"
          }
        } catch (err) {
          console.error("❌ Failed to fetch user:", err)
        }

        return {
          id: docSnap.id,
          userId,
          userEmail,
          symbol: trade.symbol,
          side: trade.side,
          amount: trade.amount,
          price: trade.price,
          createdAt: trade.createdAt?.toDate?.().toLocaleString() || "-"
        } as Trade
      })

      const results = await Promise.all(promises)
      setTrades(results)

      if (snapshot.docs.length > 0) {
        if (direction === "next" && cursor) {
          setPageStack((prev) => [...prev, cursor])
        } else if (direction === "prev") {
          setPageStack((prev) => prev.slice(0, -1))
        }
        setLastDoc(snapshot.docs[snapshot.docs.length - 1])
      }
    } catch (err) {
      console.error("❌ Failed to fetch trades:", err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="trades-panel">
      <h2>All Trades</h2>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Symbol</th>
                <th>Side</th>
                <th>Amount</th>
                <th>Price</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 ? (
                <tr>
                  <td colSpan={6}>No trades found</td>
                </tr>
              ) : (
                trades.map((t) => (
                  <tr key={t.id}>
                    <td>{t.userEmail}</td>
                    <td>{t.symbol}</td>
                    <td className={t.side === "buy" ? "buy" : "sell"}>{t.side}</td>
                    <td>{t.amount}</td>
                    <td>${t.price}</td>
                    <td>{t.createdAt}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="pagination">
            <button
              disabled={pageStack.length === 0}
              onClick={() => loadTrades(pageStack[pageStack.length - 1], "prev")}
            >
              Previous
            </button>
            <button
              disabled={!lastDoc}
              onClick={() => lastDoc && loadTrades(lastDoc, "next")}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  )
}

export default TradesPanel
